import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Eye, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { AddFacilityPartyModal } from "./add-facility-party-modal";

interface FacilityParty {
  id: string;
  facilityId: string;
  partyType: "account" | "contact";
  accountId?: string | null;
  contactId?: string | null;
  partyRole: string;
  ownershipPercentage?: string | null;
  isPrimary?: boolean | null;
  status?: string | null;
  notes?: string | null;
  partyName?: string; // ISSUE-133: Resolved party name from API
}

interface FacilityPartiesPanelProps {
  facilityId: string;
  clientGroupId?: string;
  dealId?: string;
}

export function FacilityPartiesPanel({ facilityId, clientGroupId, dealId }: FacilityPartiesPanelProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isAddPartyModalOpen, setIsAddPartyModalOpen] = useState(false);
  const [editingParty, setEditingParty] = useState<FacilityParty | null>(null);

  const { data: parties = [], isLoading, refetch } = useQuery<FacilityParty[]>({
    queryKey: [`/api/crm/facilities/${facilityId}/parties`],
    enabled: !!facilityId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (partyId: string) => {
      await apiRequest("DELETE", `/api/crm/facilities/${facilityId}/parties/${partyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/facilities/${facilityId}/parties`] });
      // ISSUE-149 REWORK #3: Use exact query key (Investigation Agent finding)
      // Exact key works because it matches deal-parties-panel.tsx line 68 format
      // Wildcard pattern failed due to staleTime: Infinity preventing refetch of inactive queries
      if (dealId) {
        queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      }
      toast({
        title: "Party removed",
        description: "The party has been removed from this facility.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove party",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (partyId: string) => {
    if (confirm("Are you sure you want to remove this party from the facility?")) {
      deleteMutation.mutate(partyId);
    }
  };

  const handleView = (partyId: string | null | undefined, partyType: string) => {
    if (!partyId) return;
    const route = partyType === "contact" ? `/contacts/${partyId}` : `/accounts/${partyId}`;
    navigate(route);
  };

  const getRoleBadgeVariant = (role: string) => {
    if (role === "borrower") return "default";
    if (role === "co_borrower") return "secondary";
    if (role === "guarantor") return "outline";
    return "secondary";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Facility Parties</h3>
        <Button size="sm" variant="outline" onClick={() => setIsAddPartyModalOpen(true)}>
          <Plus className="h-3 w-3 mr-1" />
          Add Party
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="pt-3">
            <p className="text-sm text-muted-foreground text-center py-4">Loading parties...</p>
          </CardContent>
        </Card>
      ) : parties.length === 0 ? (
        <Card>
          <CardContent className="pt-3">
            <p className="text-sm text-muted-foreground text-center py-4">
              No parties added yet. Parties link borrowers, guarantors, and security providers to this facility.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="h-8 bg-muted/50">
                  <TableHead className="px-2 py-1 text-xs font-medium">Type</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Party</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Role</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Ownership %</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Primary</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parties.map((party) => {
                  const partyId = party.partyType === "account" ? party.accountId : party.contactId;
                  return (
                    <TableRow key={party.id} className="h-8">
                      <TableCell className="px-2 py-1">
                        <Badge variant="outline" className="text-xs capitalize">
                          {party.partyType}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm font-medium">
                        <button
                          onClick={() => handleView(partyId, party.partyType)}
                          className="text-primary hover:underline text-left"
                        >
                          {party.partyName || "Unknown Party"}
                        </button>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge variant={getRoleBadgeVariant(party.partyRole)} className="text-xs capitalize">
                          {party.partyRole.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm text-right tabular-nums">
                        {party.ownershipPercentage != null ? `${party.ownershipPercentage}%` : "—"}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-center">
                        {party.isPrimary && <Badge variant="default" className="text-xs">Primary</Badge>}
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleView(partyId, party.partyType)}
                            title="View party"
                            disabled={!partyId}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingParty(party);
                              setIsAddPartyModalOpen(true);
                            }}
                            title="Edit party"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleDelete(party.id)}
                            title="Remove from facility"
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground mt-2">
        {parties.length} part{parties.length !== 1 ? "ies" : "y"}
      </p>

      {/* Add/Edit Party Modal */}
      <AddFacilityPartyModal
        open={isAddPartyModalOpen}
        onOpenChange={(open) => {
          setIsAddPartyModalOpen(open);
          if (!open) {
            setEditingParty(null); // Clear editing party when modal closes
          }
        }}
        facilityId={facilityId}
        clientGroupId={clientGroupId}
        dealId={dealId} // ISSUE-149 REWORK #3: Pass dealId for exact cache invalidation
        party={editingParty}
        onPartyAdded={() => {
          refetch();
          setEditingParty(null);
        }}
      />
    </div>
  );
}
